import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  useToast,
  useColorModeValue,
  Icon,
  Divider,
  Switch,
  FormControl,
  FormLabel,
  Heading,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Button,
} from '@chakra-ui/react';
import { FiBell, FiCheckCircle, FiAlertCircle, FiClock, FiSunrise } from 'react-icons/fi';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { canAccessNewFeatures } from '../../../utils/featureAccess';

export default function NotificationSettings({ userId }) {
  const toast = useToast();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBG = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (!userId) return;

    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        setUserData(docSnap.data());
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  const notificationSettings = userData?.crmNotificationSettings || {
    newLeadAssignments: true,
    atRiskDeals: true,
    taskReminders: true,
    dealTransfers: true,
    dailyDigest: false,
  };

  const handleToggleSetting = async (setting, value) => {
    setSaving(true);
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        [`crmNotificationSettings.${setting}`]: value,
      });

      toast({
        title: 'Setting updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error updating notification setting:', error);
      toast({
        title: 'Error updating setting',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Center py={8}>
        <Spinner />
      </Center>
    );
  }

  const hasFCMTokens = userData?.['FCM Tokens'] && userData['FCM Tokens'].length > 0;

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="md" mb={2}>CRM Notifications</Heading>
        <Text fontSize="sm" color="gray.600">
          Choose which CRM events trigger push notifications
        </Text>
      </Box>

      <Divider />

      {!hasFCMTokens && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Push Notifications Not Enabled
            </Text>
            <Text fontSize="xs">
              Enable browser notifications to receive these alerts. Look for the banner on your My Deals page.
            </Text>
          </VStack>
        </Alert>
      )}

      {/* Notification Preferences */}
      <VStack align="stretch" spacing={4}>
        {/* New Lead Assignments */}
        <FormControl display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1} mr={4}>
            <HStack spacing={2} mb={1}>
              <Icon as={FiBell} boxSize={4} color="blue.500" />
              <FormLabel fontSize="sm" mb={0} fontWeight="semibold">
                New Lead Assignments
              </FormLabel>
            </HStack>
            <Text fontSize="xs" color="gray.600">
              Get notified when new leads are assigned to you
            </Text>
          </Box>
          <Switch
            isChecked={notificationSettings.newLeadAssignments}
            onChange={(e) => handleToggleSetting('newLeadAssignments', e.target.checked)}
            colorScheme="blue"
            size="lg"
            isDisabled={saving}
          />
        </FormControl>

        <Divider />

        {/* At-Risk Deals */}
        <FormControl display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1} mr={4}>
            <HStack spacing={2} mb={1}>
              <Icon as={FiAlertCircle} boxSize={4} color="red.500" />
              <FormLabel fontSize="sm" mb={0} fontWeight="semibold">
                At-Risk Deal Alerts
              </FormLabel>
            </HStack>
            <Text fontSize="xs" color="gray.600">
              Get notified when deals become at-risk due to inactivity or overdue actions
            </Text>
          </Box>
          <Switch
            isChecked={notificationSettings.atRiskDeals}
            onChange={(e) => handleToggleSetting('atRiskDeals', e.target.checked)}
            colorScheme="red"
            size="lg"
            isDisabled={saving}
          />
        </FormControl>

        <Divider />

        {/* Task Reminders */}
        <FormControl display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1} mr={4}>
            <HStack spacing={2} mb={1}>
              <Icon as={FiClock} boxSize={4} color="orange.500" />
              <FormLabel fontSize="sm" mb={0} fontWeight="semibold">
                Task Reminders
              </FormLabel>
            </HStack>
            <Text fontSize="xs" color="gray.600">
              Get notified about upcoming and overdue tasks
            </Text>
          </Box>
          <Switch
            isChecked={notificationSettings.taskReminders}
            onChange={(e) => handleToggleSetting('taskReminders', e.target.checked)}
            colorScheme="orange"
            size="lg"
            isDisabled={saving}
          />
        </FormControl>

        <Divider />

        {/* Deal Transfers */}
        <FormControl display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1} mr={4}>
            <HStack spacing={2} mb={1}>
              <Icon as={FiCheckCircle} boxSize={4} color="purple.500" />
              <FormLabel fontSize="sm" mb={0} fontWeight="semibold">
                Deal Transfers
              </FormLabel>
            </HStack>
            <Text fontSize="xs" color="gray.600">
              Get notified when deals are transferred to or from you
            </Text>
          </Box>
          <Switch
            isChecked={notificationSettings.dealTransfers}
            onChange={(e) => handleToggleSetting('dealTransfers', e.target.checked)}
            colorScheme="purple"
            size="lg"
            isDisabled={saving}
          />
        </FormControl>

        <Divider />

        {/* Daily Digest */}
        <FormControl display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1} mr={4}>
            <HStack spacing={2} mb={1}>
              <Icon as={FiBell} boxSize={4} color="cyan.500" />
              <FormLabel fontSize="sm" mb={0} fontWeight="semibold">
                Daily Digest (8 AM)
              </FormLabel>
            </HStack>
            <Text fontSize="xs" color="gray.600">
              Receive a daily summary of your pipeline and upcoming tasks
            </Text>
          </Box>
          <Switch
            isChecked={notificationSettings.dailyDigest}
            onChange={(e) => handleToggleSetting('dailyDigest', e.target.checked)}
            colorScheme="cyan"
            size="lg"
            isDisabled={saving}
          />
        </FormControl>

        {/* FutureBot Daily Briefing email (admin preview) */}
        {canAccessNewFeatures({ currentUser: userData }) && (
          <>
            <Divider />

            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box flex={1} mr={4}>
                <HStack spacing={2} mb={1}>
                  <Icon as={FiSunrise} boxSize={4} color="teal.500" />
                  <FormLabel fontSize="sm" mb={0} fontWeight="semibold">
                    Daily Briefing Email (7 AM)
                  </FormLabel>
                </HStack>
                <Text fontSize="xs" color="gray.600">
                  Get your FutureBot morning briefing (ranked call list, new leads, tasks, refi opportunities) by email.
                  It always appears on your Dashboard and in the notification bell.
                </Text>
              </Box>
              <Switch
                isChecked={notificationSettings.dailyBriefing === true}
                onChange={(e) => handleToggleSetting('dailyBriefing', e.target.checked)}
                colorScheme="teal"
                size="lg"
                isDisabled={saving}
              />
            </FormControl>
          </>
        )}
      </VStack>

      {/* Info Box */}
      <Box fontSize="xs" color="gray.500" p={4} bg={cardBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <Text fontWeight="semibold" mb={2}>About Notifications</Text>
        <VStack align="flex-start" spacing={1}>
          <Text>
            • Notifications appear as push notifications in your browser
          </Text>
          <Text>
            • Click on a notification to navigate directly to the relevant page
          </Text>
          <Text>
            • You can manage browser notification permissions in your browser settings
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
}

